<template>

  <v-toolbar-items class="d-flex align-center justify-center">
    <v-combobox class="wgu-geocoder-combo wgu-solo-field custom-combobox-left" outlined dense color="accent"
      :dark="isPrimaryDark" filled return-object hide-details :autofocus="autofocus" :items="selectItems"
      v-model="selectedItem" label="分类" :persistent-hint="persistentHint" :rounded="rounded">
    </v-combobox>
    <v-autocomplete class="wgu-geocoder-combo wgu-solo-field custom-combobox-middle" outlined dense color="accent"
      :dark="isPrimaryDark" filled return-object hide-details :items="resultItems" v-model="selected"
      :autofocus="autofocus" :label="$t('wgu-geocoder.placeHolder')" :clearable="clearable"
      :persistent-hint="persistentHint" :hidden="hideSearch" :rounded="rounded" :search-input.sync="search"
      @compositionstart="handleCompositionStart" @compositionend="handleCompositionEnd" @change="areaInputChange">
    </v-autocomplete>
    <v-combobox class="wgu-geocoder-combo wgu-solo-field custom-combobox-right" outlined dense color="accent"
      :dark="isPrimaryDark" filled return-object hide-details :autofocus="autofocus" :items="selectYears"
      v-model="selectedYear" label="年份" :persistent-hint="persistentHint" :rounded="rounded"
      @change="updateSelectedYear">
    </v-combobox>
  </v-toolbar-items>
</template>

<script>
import { Mapable } from '../../mixins/Mapable';
import { ColorTheme } from '../../mixins/ColorTheme';
import { GeocoderController } from './GeocoderController';
import { applyTransform } from 'ol/extent';
import { getTransform, fromLonLat } from 'ol/proj';
import ViewAnimationUtil from '../../util/ViewAnimation';

export default {
  name: 'wgu-geocoder-input',
  mixins: [Mapable, ColorTheme],
  props: {
    icon: { type: String, required: false, default: 'search' },
    rounded: { type: Boolean, required: false, default: true },
    autofocus: { type: Boolean, required: false, default: true },
    clearable: { type: Boolean, required: false, default: true },
    persistentHint: { type: Boolean, required: false, default: true },
    debug: { type: Boolean, required: false, default: false },
    minChars: { type: Number, required: false, default: 3 },
    queryDelay: { type: Number, required: false, default: 300 },
    provider: { type: String, required: false, default: 'osm' },
    providerOptions: { type: Object, required: false, default: function () { return {}; } }

  },
  data () {
    return {
      results: [],
      lastQueryStr: '',
      search: null,
      selecting: false,
      syncingSelectedFromStore: false,
      selected: null,
      hideSearch: false,
      timeout: null,
      isComposing: false,
      selectItems: [
        { text: '行政区', value: 'adminRegion' },
        { text: '地块', value: 'landParcel' },
        { text: '人员', value: 'personnel' }
      ],
      selectedItem: null,
      selectYears: [
        { text: '2026年', value: '2026' },
        { text: '2025年', value: '2025' },
        { text: '2024年', value: '2024' }
      ],
      selectedYear: 2026
    }
  },
  created () {
    this.selectedItem = this.selectItems.find(item => item.value === 'adminRegion');
    this.selectedYear = this.selectYears.find(item => item.value === '2026');
    this.$store.commit('setSelectedYear', this.selectedYear);
  },
  mounted () {
    // Setup GeocoderController to which we delegate Provider and query-handling
    this.geocoderController = new GeocoderController(this.provider, this.providerOptions, this);
    // 在mounted中获取初始数据，确保组件完全初始化后再加载数据
    this.getOptions();
  },
  computed: {
    selectedArea () {
      return this.$store.state.selectedArea;
    },
    authorizedArea () {
      // Use the actual state property, not the mutation function
      return this.$store.state.authorizedArea;
    },
    resultItems () {
      const items = [];
      if (!this.results) {
        return items;
      }
      console.log(`Fetch Region results: len=${this.results.length}`, this.results);

      // Convert results to v-combobox (text, value) Items
      this.results.forEach((result, index) => {
        // Be robust to various backend payloads
        const regionName = result.display_name || result.name || result.text || result.village_name || result.town_name || result.label || result.title;
        items.push({ text: regionName, value: result });
      });

      console.log('Final resultItems:', items);
      return items;
    }
  },
  methods: {
    trace (str) {
      this.debug && console && console.info(str);
    },
    toggle () {
      // Show/hide search combobox
      // to do other things, e.g. show/hide map
      // this.hideSearch = !this.hideSearch;
    },
    // Query by string - should return list of selection items (adresses) for ComboBox
    querySelections (queryStr) {
      this.timeout = setTimeout(() => {
        // Let Geocoder Provider do the query
        // items (item.text fields) will be shown in combobox dropdown suggestions
        this.trace(`geocoderController.query: ${queryStr}`);
        this.geocoderController.query(queryStr, this.selectedItem.value)
          .then(results => this.onQueryResults(results))
          .catch(err => this.onQueryError(err))
      }, this.queryDelay);
    },
    onQueryResults (results) {
      // Handle query results from GeocoderController
      this.timeout && clearTimeout(this.timeout);
      this.timeout = null;

      console.log('onQueryResults called with:', results);
      if (!results || results.length === 0) {
        console.log('No query results, keeping current results');
        // 不要清空现有的结果，特别是在选择操作后
        if (!this.selecting) {
          this.results = [];
        }
        return;
      }

      // ASSERT: results is defined and at least one result
      this.trace(`results ok: len=${results.length}`);
      console.log('Setting new query results:', results);
      this.results = results;
    },
    onQueryError (err) {
      if (err) {
        this.trace(`onQueryResult error: ${err}`);
      }
    },
    handleCompositionStart () {
      this.isComposing = true;
    },
    handleCompositionEnd () {
      this.isComposing = false;
      // filter items based on this.selected
      this.triggerSearch(this.search);
    },
    triggerSearch (queryStr) {
      if (!queryStr || queryStr.length < this.minChars) {
        return;
      }
      // ASSERTION: queryStr is valid
      // queryStr changed by selection type change (e.g. 'administrative_division')
      queryStr = queryStr.trim();
      this.querySelections(queryStr);
      this.lastQueryStr = queryStr;
    },
    updateSelectedYear (value) {
      console.log('updateSelectedYear', value);
      this.$store.commit('setSelectedYear', value);
    },
    getAreaCode (value) {
      if (!value || typeof value !== 'object') return null;
      return value.place_id || value.code || value?.address?.code || value?.address?.place_id || null;
    },
    buildSelectedItem (area) {
      if (!area || typeof area !== 'object') return null;
      const text = area.display_name || area.name || area.text || area.village_name || area.town_name || '';
      return { text, value: area };
    },
    syncSelectedFromStore (area) {
      const nextCode = this.getAreaCode(area);
      const currentCode = this.getAreaCode(this.selected && this.selected.value);

      if (!area) {
        if (!this.selected) return;
        this.syncingSelectedFromStore = true;
        this.selected = null;
        this.$nextTick(() => {
          this.syncingSelectedFromStore = false;
        });
        return;
      }

      if (nextCode && currentCode && nextCode === currentCode) {
        return;
      }

      const nextSelected = this.buildSelectedItem(area);
      this.syncingSelectedFromStore = true;
      this.selected = nextSelected;
      this.$nextTick(() => {
        this.syncingSelectedFromStore = false;
      });
    },
    areaInputChange (value) {
      console.log('areaInputChange called with:', value, 'Type:', typeof value);
      if (!value) {
        // 当 v-combobox 被清空时执行的逻辑
        console.log('Clearing selected area');
        this.$store.commit('setSelectedArea', null);
        console.log('Combobox cleared');
      } else {
        console.log('Area selected:', value);
      }
    },
    /**
     * Fetch the auth regions from the server.
     */
    async getOptions () {
      try {
        if (Array.isArray(this.$store.state.authorizedArea) && this.$store.state.authorizedArea.length > 0) {
          this.results = this.$store.state.authorizedArea;
        }

        // Use centralized Vuex action so multiple components share the same fetch/cache logic
        const data = await this.$store.dispatch('fetchAuthRegions');
        // fetchAuthRegions returns an object: { results: [...], auth_region: [...] }
        let list = [];
        if (Array.isArray(data)) {
          list = data;
        } else if (data && Array.isArray(data.results)) {
          list = data.results;
        } else if (Array.isArray(this.$store.state.authorizedArea)) {
          list = this.$store.state.authorizedArea;
        }
        if (!Array.isArray(list)) list = [];
        if (list.length > 0) {
          console.log('First auth region structure:', list[0]);
        }
        this.results = list;
        console.log('Auth Regions Result:', this.results);
      } catch (error) {
        this.results = [];
      }
    }
  },
  watch: {
    selectedArea: {
      handler (newArea) {
        this.syncSelectedFromStore(newArea);
      },
      deep: true,
      immediate: true
    },
    authorizedArea: {
      handler (newAreas) {
        if (this.selectedItem && this.selectedItem.value !== 'adminRegion') {
          return;
        }

        if (Array.isArray(newAreas)) {
          this.results = newAreas;
        } else {
          this.results = [];
        }
      },
      deep: true,
      immediate: true
    },
    // 监听分类选择变化
    selectedItem (newItem, oldItem) {
      if (newItem && oldItem && newItem.value !== oldItem.value) {
        // 分类改变时，重新获取初始数据
        console.log('Category changed from', oldItem.value, 'to', newItem.value);
        if (newItem.value === 'adminRegion') {
          this.getOptions();
        } else {
          // 其他分类暂时清空结果，等待用户搜索
          this.results = [];
        }
        // 清空当前选择
        this.selected = null;
        this.$store.commit('setSelectedArea', null);
      }
    },
    // Input string value changed
    search (queryStr) {
      console.log('Search triggered with:', queryStr, 'isComposing:', this.isComposing, 'selecting:', this.selecting);
      if (this.isComposing) {
        // Composition in progress
        this.trace('composition in progress...');
        console.log('composition in progress...');
        return;
      }
      if (this.selecting) {
        // Query or selection in progress
        this.trace('query or selection in progress...');
        console.log('selection in progress, skipping search');
        return;
      }

      // 对于行政区分类，使用前端过滤，不触发后端查询
      if (this.selectedItem && this.selectedItem.value === 'adminRegion') {
        console.log('Admin region mode: using frontend filtering');
        if (!queryStr || queryStr.length === 0) {
          console.log('Empty search string, restoring initial auth regions');
          this.getOptions();
        }
        // v-autocomplete 会自动处理前端过滤，无需后端查询
        return;
      }

      // 对于其他分类（地块、人员），继续使用后端查询
      if (!queryStr || queryStr.length === 0) {
        this.trace('queryStr none');
        console.log('Empty search string');
        this.results = [];
        return;
      }

      queryStr = queryStr.trim();
      console.log('Processing search query:', queryStr, 'lastQuery:', this.lastQueryStr);

      if (this.selected && this.selected.text === queryStr) {
        console.log('Search triggered by selection');
        return;
      }

      // Only query if minimal number chars typed and querystring has changed
      if (queryStr.length >= this.minChars && queryStr !== this.lastQueryStr) {
        console.log('Executing backend query for:', queryStr);
        this.querySelections(queryStr);
      } else {
        console.log('Skipping query - too short or same as last:', queryStr.length, this.minChars, queryStr === this.lastQueryStr);
      }
      this.lastQueryStr = queryStr;
    },
    // User has selected entry from suggested items
    selected: {
      handler: function onResultSelected (item) {
        if (this.syncingSelectedFromStore) {
          return;
        }
        console.log('Selected item:', item);
        if (!item || !Object.prototype.hasOwnProperty.call(item, 'text') || !Object.prototype.hasOwnProperty.call(item, 'value')) {
          console.log('Invalid item selected, returning');
          return;
        }
        // 设置选择状态，阻止搜索干扰
        this.selecting = true;
        this.trace(`selected=${item.text}`);
        console.log(`Selected: ${item.text}`, item.value);

        // Position Map on result
        const result = item.value;
        console.log('Processing selected result:', result);
        const view = this.map.getView();
        const mapProjection = view.getProjection();

        // 归一化坐标与包围盒
        const lon = result?.lon != null ? parseFloat(result.lon) : null;
        const lat = result?.lat != null ? parseFloat(result.lat) : null;
        const hasBbox = Array.isArray(result?.boundingbox) && result.boundingbox.length === 4;
        const bbox4326 = hasBbox ? result.boundingbox.map(v => parseFloat(v)) : null;

        // 动画结束后的回调：提交到 store 并解除 selecting 状态
        const finish = () => {
          // 提交选择到 store（放到动画完成后，避免其他监听立即打断动画）
          this.$store.commit('setSelectedArea', item.value);
          console.log('Selection completed, keeping all results visible');
          this.$nextTick(() => {
            this.selecting = false;
            this.lastQueryStr = '';
            console.log('Selection state reset, ready for next search');
          });
        };

        try {
          if (hasBbox && bbox4326.every(Number.isFinite)) {
            // Result with bounding box in EPSG:4326, transform to map projection
            const extent = applyTransform(bbox4326.slice(), getTransform('EPSG:4326', mapProjection));
            ViewAnimationUtil.to(view, extent, finish);
            return;
          }
          if (Number.isFinite(lon) && Number.isFinite(lat)) {
            // No bbox in result: center on lon/lat from result and zoom in
            const coords = fromLonLat([lon, lat], mapProjection);
            ViewAnimationUtil.to(view, coords, finish);
            return;
          }
          console.log('No valid coordinates or bounding box in result:', result);
        } catch (e) {
          console.warn('Error while animating to selection, falling back without animation:', e);
        }

        // 无有效空间信息或动画异常时：直接提交并复位状态
        finish();
      }
    }
  }
}
</script>
<style scoped>
.custom-combobox-middle {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;

}

.custom-combobox-right {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-top-right-radius: 18px;
  border-bottom-right-radius: 18px;
  width: 130px;
}

.custom-combobox-left {
  border-top-left-radius: 18px;
  border-bottom-left-radius: 18px;
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
  width: 120px;
}
</style>
